import { normalizeGenbaName } from './genbaNameMatch';

/**
 * Locked contract for 現場賞与 (construction-site bonus).
 * Sample file: 賞与_柏崎原発作業所_2026_07.txt
 *
 * Do not write these rows into construction_salary_data or 本社 bonus_data.
 */

export const CONSTRUCTION_BONUS_FILE_PREFIX = '賞与';
export const CONSTRUCTION_BONUS_API_BASE_PATH = '/construction-bonus';
export const CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE = 'construction-bonus';

/** Company bonus months (July / November). UI dropdown is fixed to these. */
export const CONSTRUCTION_BONUS_MONTH_OPTIONS = ['7', '11'] as const;

export function filterConstructionBonusMonths(periods: string[]): string[] {
  const allowed = new Set<string>(CONSTRUCTION_BONUS_MONTH_OPTIONS);
  return periods.filter((p) => allowed.has(String(p).replace('月', '').trim()));
}

/** Exact header names from the 現場賞与 export (15 columns). */
export const CONSTRUCTION_BONUS_HEADERS = [
  '名前',
  '賞与',
  '支給合計',
  '健康保険',
  '雇用保険',
  '厚生年金',
  '所得税',
  '控除合計',
  '差引支給額',
  '振込金額',
  'その他控除',
  'その他支給',
  'その他支給内訳',
  'その他控除内訳',
  '備考',
] as const;

export type ConstructionBonusHeader = (typeof CONSTRUCTION_BONUS_HEADERS)[number];

export interface ConstructionBonusFilenameParts {
  genbaName: string;
  year: number;
  /** Canonical month period "1".."12" with no leading zero. Filename 07 → "7". */
  bonusPeriod: string;
}

export function canonicalBonusPeriod(month: number): string {
  if (month < 1 || month > 12 || !Number.isInteger(month)) {
    throw new Error(`Bonus period month must be 1-12, got: ${month}`);
  }
  return String(month);
}

/**
 * Filename: 賞与_{現場名}_{YYYY}_{MM}.txt (or .csv).
 * Site name may contain underscores; year and month are the last two segments.
 */
export function parseConstructionBonusFilename(
  filename: string | null | undefined
): ConstructionBonusFilenameParts | null {
  if (filename == null || filename.trim() === '') {
    return null;
  }
  const base = filename.replace(/\\/g, '/').split('/').pop() ?? '';
  if (!/\.(txt|csv)$/i.test(base)) {
    return null;
  }
  const nameWithoutExt = base.replace(/\.(txt|csv)$/i, '');
  const parts = nameWithoutExt.split('_');
  if (parts.length < 4 || parts[0] !== CONSTRUCTION_BONUS_FILE_PREFIX) {
    return null;
  }

  const monthToken = parts[parts.length - 1].split(' ')[0];
  const yearToken = parts[parts.length - 2];
  const year = parseInt(yearToken, 10);
  const month = parseInt(monthToken, 10);
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    year <= 2000 ||
    year >= 2100 ||
    month < 1 ||
    month > 12
  ) {
    return null;
  }

  const genbaName = normalizeGenbaName(parts.slice(1, -2).join('_'));
  if (!genbaName) {
    return null;
  }
  return { genbaName, year, bonusPeriod: canonicalBonusPeriod(month) };
}

export function isConstructionBonusHeaderRow(headers: string[]): boolean {
  const set = new Set(headers.map((h) => h.trim().replace(/^"|"$/g, '')));
  return CONSTRUCTION_BONUS_HEADERS.every((required) => set.has(required));
}

export interface ConstructionBonusPreviewRow {
  employeeName: string;
  bonus: number;
  totalPayment: number;
  netPaymentAmount: number;
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += c;
    }
  }
  result.push(current.trim());
  return result;
}

function parseNumberCell(value: string | undefined): number {
  if (value == null || value.trim() === '') {
    return 0;
  }
  const cleaned = value.replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Parse 現場賞与 CSV/TXT body (quoted comma-separated, 15 headers).
 */
export function parseConstructionBonusCsv(content: string): ConstructionBonusPreviewRow[] {
  const text = content.replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length < 2) {
    throw new Error('賞与ファイルにヘッダーとデータ行がありません。');
  }

  const headers = parseCsvLine(lines[0]).map((h) => h.replace(/^"|"$/g, '').trim());
  const headerSet = new Set(headers);
  // Day columns identify 現場給与 even though both formats now use 健康保険.
  if (headerSet.has('1日')) {
    throw new Error('このファイルは現場給与です。賞与データとしてアップロードできません。');
  }
  if (headerSet.has('業績賞与') || headerSet.has('賞与ロック')) {
    throw new Error('このファイルは本社賞与です。現場の賞与データとしてアップロードできません。');
  }
  if (!isConstructionBonusHeaderRow(headers)) {
    throw new Error(
      '賞与ファイルのヘッダーが認識できません。支給合計・控除合計・差引支給額・振込金額を含む15列の賞与CSVをアップロードしてください。'
    );
  }

  const index = (name: string) => headers.indexOf(name);
  const nameIdx = index('名前');
  const bonusIdx = index('賞与');
  const totalIdx = index('支給合計');
  const netIdx = index('差引支給額');

  const rows: ConstructionBonusPreviewRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]).map((c) => c.replace(/^"|"$/g, ''));
    const employeeName = (cells[nameIdx] ?? '').trim();
    if (!employeeName) {
      continue;
    }
    rows.push({
      employeeName,
      bonus: parseNumberCell(cells[bonusIdx]),
      totalPayment: parseNumberCell(cells[totalIdx]),
      netPaymentAmount: parseNumberCell(cells[netIdx]),
    });
  }
  if (rows.length === 0) {
    throw new Error('賞与ファイルから有効なレコードを読み取れませんでした。');
  }
  return rows;
}

/** Stored/API row for 現場賞与 (construction_bonus_data). */
export interface ConstructionBonusDataRow {
  genbaName: string;
  employeeName: string;
  year: number;
  bonusPeriod: string;
  bonus: number;
  totalPayment: number;
  healthInsurance: number;
  employmentInsurance: number;
  pensionInsurance: number;
  incomeTax: number;
  deductionTotal: number;
  netPaymentAmount: number;
  transferAmount: number;
  otherDeduction: number;
  otherPayment: number;
  otherPaymentBreakdown?: string;
  otherDeductionBreakdown?: string;
  remarks?: string;
}

function csvQuote(value: string | number | null | undefined): string {
  const s = value == null ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * Export 現場賞与 rows as the 15-column quoted CSV (same headers as the source file).
 */
export function constructionBonusRowsToCsv(
  rows: ConstructionBonusDataRow[],
  includeHeaders = true
): string {
  const lines: string[] = [];
  if (includeHeaders) {
    lines.push(CONSTRUCTION_BONUS_HEADERS.map((h) => csvQuote(h)).join(','));
  }
  for (const row of rows) {
    lines.push(
      [
        csvQuote(row.employeeName),
        csvQuote(row.bonus ?? 0),
        csvQuote(row.totalPayment ?? 0),
        csvQuote(row.healthInsurance ?? 0),
        csvQuote(row.employmentInsurance ?? 0),
        csvQuote(row.pensionInsurance ?? 0),
        csvQuote(row.incomeTax ?? 0),
        csvQuote(row.deductionTotal ?? 0),
        csvQuote(row.netPaymentAmount ?? 0),
        csvQuote(row.transferAmount ?? 0),
        csvQuote(row.otherDeduction ?? 0),
        csvQuote(row.otherPayment ?? 0),
        csvQuote(row.otherPaymentBreakdown ?? ''),
        csvQuote(row.otherDeductionBreakdown ?? ''),
        csvQuote(row.remarks ?? ''),
      ].join(',')
    );
  }
  return lines.join('\n');
}

/**
 * Download name: 賞与_{現場名}_{YYYY}_{MM}.csv (MM padded). Missing filters become "all".
 */
export function constructionBonusDownloadFilename(
  genbaName?: string,
  year?: string | number,
  period?: string
): string {
  const site = genbaName && genbaName.trim() !== '' ? genbaName.replace(/\s+/g, '') : 'all';
  const y = year != null && String(year) !== '' ? String(year) : 'all';
  let mm = 'all';
  if (period != null && period !== '') {
    const n = parseInt(period, 10);
    mm = Number.isInteger(n) && n >= 1 && n <= 12 ? String(n).padStart(2, '0') : period;
  }
  return `${CONSTRUCTION_BONUS_FILE_PREFIX}_${site}_${y}_${mm}.csv`;
}
